<!-- eslint-disable max-len -->
<template>
  <div>
    <WdsText secondary class="tw-mb-4">
      {{ $t('Please upload your company legal documentation here.') }}
    </WdsText>

    <WdsText secondary class="tw-mb-4">
      {{
        $t(
          "Are you listed as a natural person on the Chamber of Commerce extract as a director and (major) shareholder? If no, upload the Chamber of Commerce document of the company mentioned as the 'eigenaar' / owner on the document above.",
        )
      }}
    </WdsText>

    <div
      v-for="slot in fileSlots"
      :key="slot.index"
      class="tw-mb-4 tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg"
    >
      <WdsText weight="bold" class="tw-mb-2">
        {{ slot.label }}
      </WdsText>

      <PickerFileFilebank
        v-slot="{ openFilePicker }"
        :default-tags="['kvk-certificate']"
        @files-selected="onFilesSelected(slot.index, $event)"
      >
        <WdsButton type="button" variant="secondary" @click="openFilePicker">
          {{ currentFileId(slot.index) ? $t('Replace document') : $t('Choose file') }}
        </WdsButton>
      </PickerFileFilebank>

      <div
        v-if="displayFileName(slot.index)"
        class="tw-flex tw-items-center tw-gap-1 tw-mt-2"
      >
        <WdsText secondary size="small">
          {{ displayFileName(slot.index) }}
        </WdsText>
        <a
          v-if="currentFileId(slot.index)"
          :href="`/files/${currentFileId(slot.index)}`"
          target="_blank"
          rel="noopener noreferrer"
        >
          <WdsIcon name="download" :size="16" />
        </a>
      </div>
    </div>

    <WdsAlert
      v-if="errorMessage && showError"
      class="tw-mt-4"
      closable
      error
      @close="showError = false"
    >
      {{ $t('An error occurred') }}
      <template #description>{{ errorMessage }}</template>
    </WdsAlert>

    <WdsAlert v-else-if="showSuccess" class="tw-mt-4" closable success @close="showSuccess = false">
      {{ $t('Your KvK documents have been saved') }}
    </WdsAlert>

    <div class="tw-mt-4">
      <WdsButton type="button" :disabled="saving || !hasPrimaryDocument" primary @click="onSave">
        <div class="tw-flex tw-items-center">
          <span>{{ $t('Save') }}</span>
          <WdsSpinner v-show="saving" class="tw-ml-2" indeterminate>
            <WdsIcon name="bam" :size="16" />
          </WdsSpinner>
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import {
  WdsAlert,
  WdsButton,
  WdsIcon,
  WdsSpinner,
  WdsText,
} from '@worksome/wds-components'
import PickerFileFilebank from '@/components/PickerFileFilebank.vue'
import { UpdateComplianceMutation } from '@/components/compliances/mutations/UpdateComplianceMutation'
import { FormValidation } from '@/utils/formValidation'

const KVK_DOCUMENT_SLOT_COUNT = 4

function normalizeSavedFiles(complianceData) {
  if (!complianceData) {
    return []
  }

  if (Array.isArray(complianceData.fileIds) && complianceData.fileIds.length > 0) {
    const fileNames = Array.isArray(complianceData.fileNames) ? complianceData.fileNames : []

    return complianceData.fileIds
      .map((id, index) => ({ id, name: fileNames[index] ?? null }))
      .filter((file) => file.id)
  }

  return []
}

export default {
  name: 'NlKvkCertificateForm',
  components: {
    PickerFileFilebank,
    WdsAlert,
    WdsButton,
    WdsIcon,
    WdsSpinner,
    WdsText,
  },
  props: {
    complianceName: {
      type: String,
      required: true,
    },
    complianceData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['saved'],
  data() {
    const savedFiles = normalizeSavedFiles(this.complianceData)

    return {
      pendingFiles: Array.from({ length: KVK_DOCUMENT_SLOT_COUNT }, () => null),
      savedFiles,
      saving: false,
      errors: [],
      showError: false,
      showSuccess: false,
    }
  },
  computed: {
    errorMessage() {
      return FormValidation.containsErrorMessage(this.errors)
    },
    fileSlots() {
      return Array.from({ length: KVK_DOCUMENT_SLOT_COUNT }, (_, index) => ({
        index,
        label:
          index === 0
            ? this.$t('KvK Extract / Primary Document *')
            : this.$t('Additional Supporting Document'),
      }))
    },
    hasPrimaryDocument() {
      return this.currentFileId(0) !== null
    },
    normalizedFileIds() {
      return this.getCurrentFiles()
        .filter((file) => file?.id)
        .map((file) => file.id)
    },
  },
  methods: {
    currentFileId(index) {
      return this.pendingFiles[index]?.id ?? this.savedFiles[index]?.id ?? null
    },
    displayFileName(index) {
      return this.pendingFiles[index]?.name ?? this.savedFiles[index]?.name ?? null
    },
    getCurrentFiles() {
      return this.pendingFiles.map((pendingFile, index) => pendingFile ?? this.savedFiles[index] ?? null)
    },
    onFilesSelected(index, files) {
      const file = files[0]
      if (!file) return

      this.pendingFiles[index] = {
        id: file.localId,
        name: file.name,
      }
      this.showError = false
    },

    async onSave() {
      if (!this.hasPrimaryDocument) {
        return
      }

      this.saving = true

      try {
        await this.$apollo.mutate({
          mutation: UpdateComplianceMutation,
          variables: {
            input: {
              name: this.complianceName,
              data: JSON.stringify({ file_ids: this.normalizedFileIds }),
            },
          },
        })

        this.showSuccess = true
        this.showError = false
        this.savedFiles = this.getCurrentFiles()
        this.pendingFiles = Array.from({ length: KVK_DOCUMENT_SLOT_COUNT }, () => null)
        this.$emit('saved')
      } catch ({ graphQLErrors }) {
        this.errors = graphQLErrors
        this.showError = true
        this.showSuccess = false
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
